/**
 * The first module the page runs, and deliberately almost nothing: one question, two doors.
 *
 * `index.html` is served at the console's redirect URI, and two different things arrive there. A
 * person's tab — to use the console, or on the way back from signing in — and the hidden frame MSAL
 * opens to continue a session without one (`auth/redirectBridge.ts` has the whole argument). The
 * frame wants a few lines that hand Entra's answer to the page that is waiting for it; booting a
 * console in there would fetch the whole bundle, hand over nothing, and leave that page waiting.
 *
 * **Both doors are dynamic imports, and that is the point of this file.** A static import of
 * `main.ts` would put the console — and its stylesheets — in the graph of the module the frame
 * loads. As written, the frame downloads this file and the bridge, and a tab downloads this file
 * and the console. The cost to a tab is one more small request before the console's own
 * (`vite.config.ts`).
 *
 * Nothing here reads the response or clears it. In a tab it is still in the address when
 * `handleRedirectPromise` looks for it (`auth/msal.ts`); in the frame the bridge takes it.
 */

import { isRedirectBridgeDocument } from "./auth/redirectBridge.js";

const place = { framed: window.parent !== window, hash: window.location.hash, search: window.location.search };

if (isRedirectBridgeDocument(place)) {
  // A response the bridge cannot read is not caught: the frame is hidden and has nobody to tell.
  // The browser logs the rejection, and the page that made the frame stops waiting by itself and
  // takes its own way out (`auth/tokenRenewal.ts`).
  void import("@azure/msal-browser/redirect-bridge").then((bridge) => bridge.broadcastResponseToMainFrame());
} else {
  // `main.ts` boots on import and paints its own failures. A console that cannot be fetched at all
  // is the browser's to report, as it was when `index.html` named that file itself.
  void import("./main.js");
}
